import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Check, ChevronDown } from "lucide-react";

/**
 * Select COSMOS — o único seletor de negócio e navegação da V2 (AGENTS §14.3,
 * §14.4). O elemento de seleção nativo do navegador é proibido nesses fluxos e
 * há teste de contrato estático impedindo sua volta — por isso este comentário
 * não escreve a tag literal.
 *
 * Esta implementação fecha a dívida registrada em §25.7: o seletor anterior só
 * abria e fechava. Aqui o padrão de combobox somente-seleção está completo —
 * setas, Home, End, typeahead, Enter/Espaço, Escape, Tab, clique fora e retorno
 * de foco ao gatilho.
 *
 * O foco do DOM permanece no gatilho e a opção ativa é anunciada por
 * `aria-activedescendant`: com isso, fechar por Escape ou por clique fora nunca
 * deixa o foco perdido no documento.
 */
export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  /** Segunda linha ou contexto curto ao lado do rótulo. */
  hint?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SelectProps<T extends string = string> {
  options: Array<SelectOption<T>>;
  value: T | null;
  onChange: (value: T) => void;
  /** Nome acessível. Use `labelledBy` quando houver rótulo visível. */
  label?: string;
  labelledBy?: string;
  /** Texto quando nada está selecionado. */
  placeholder?: string;
  /** `default` é a altura de controle; `field` é a altura de campo de formulário. */
  size?: "default" | "field";
  disabled?: boolean;
  /** Motivo visível/acessível da indisponibilidade (§18). */
  disabledReason?: string;
  /** Alinha o menu pela borda direita quando o gatilho fica no fim da barra. */
  align?: "start" | "end";
  invalid?: boolean;
  describedBy?: string;
  className?: string;
  id?: string;
}

const TYPEAHEAD_RESET_MS = 500;

export function Select<T extends string = string>({
  options,
  value,
  onChange,
  label,
  labelledBy,
  placeholder = "Selecionar",
  size = "default",
  disabled = false,
  disabledReason,
  align = "start",
  invalid,
  describedBy,
  className,
  id,
}: SelectProps<T>) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const listId = `${baseId}-list`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeahead = useRef<{ text: string; at: number }>({ text: "", at: 0 });

  const selectedIndex = useMemo(
    () => options.findIndex((option) => option.value === value),
    [options, value],
  );
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const firstEnabled = useCallback(
    (from: number, step: number): number => {
      if (options.length === 0) return -1;
      for (let i = 0; i < options.length; i += 1) {
        const index = (from + step * i + options.length * options.length) % options.length;
        if (!options[index]?.disabled) return index;
      }
      return -1;
    },
    [options],
  );

  const openMenu = useCallback(
    (start: "selected" | "first" | "last") => {
      if (disabled) return;
      let index = selectedIndex;
      if (start === "first" || index < 0) index = firstEnabled(0, 1);
      if (start === "last") index = firstEnabled(options.length - 1, -1);
      // Abrir a lista começa uma busca nova: o que foi digitado com a lista
      // fechada não deve continuar acumulando depois da abertura.
      typeahead.current = { text: "", at: 0 };
      setActiveIndex(index);
      setOpen(true);
    },
    [disabled, firstEnabled, options.length, selectedIndex],
  );

  const closeMenu = useCallback((restoreFocus = true) => {
    typeahead.current = { text: "", at: 0 };
    setOpen(false);
    setActiveIndex(-1);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option || option.disabled) return;
      onChange(option.value);
      closeMenu();
    },
    [closeMenu, onChange, options],
  );

  // Clique fora fecha sem escolher nada e sem roubar o foco de onde o usuário clicou.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Mantém a opção ativa visível quando a lista rola.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.querySelector<HTMLElement>(
      `#${CSS.escape(`${baseId}-opt-${activeIndex}`)}`,
    );
    // `scrollIntoView` não existe no jsdom; a rolagem é conforto, não contrato.
    node?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, baseId, open]);

  const runTypeahead = (char: string) => {
    const now = Date.now();
    const state = typeahead.current;
    state.text = now - state.at > TYPEAHEAD_RESET_MS ? char : state.text + char;
    state.at = now;

    const query = state.text.toLowerCase();
    const from = open && activeIndex >= 0 ? activeIndex : selectedIndex;
    for (let i = 1; i <= options.length; i += 1) {
      const index = (from + i + options.length) % options.length;
      const option = options[index];
      if (!option || option.disabled) continue;
      if (option.label.toLowerCase().startsWith(query)) {
        if (open) setActiveIndex(index);
        else onChange(option.value);
        return;
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        openMenu(event.key === "ArrowUp" ? "last" : "selected");
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        openMenu("first");
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        openMenu("last");
        return;
      }
      if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        runTypeahead(event.key);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex(firstEnabled(activeIndex < 0 ? 0 : activeIndex + 1, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex(firstEnabled(activeIndex < 0 ? options.length - 1 : activeIndex - 1, -1));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(firstEnabled(0, 1));
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(firstEnabled(options.length - 1, -1));
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        commit(activeIndex);
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
        break;
      case "Tab":
        // APG: Tab confirma a opção ativa e segue para o próximo controle.
        if (activeIndex >= 0) commit(activeIndex);
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          runTypeahead(event.key);
        }
    }
  };

  const describedByValue =
    [describedBy, disabled && disabledReason ? `${baseId}-reason` : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div
      ref={rootRef}
      className={className ? `cx-select ${className}` : "cx-select"}
    >
      <button
        ref={triggerRef}
        type="button"
        id={baseId}
        role="combobox"
        className="cx-select-trigger"
        data-size={size === "field" ? "field" : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && activeIndex >= 0 ? `${baseId}-opt-${activeIndex}` : undefined
        }
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        aria-invalid={invalid || undefined}
        aria-describedby={describedByValue}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onClick={() => (open ? closeMenu() : openMenu("selected"))}
        onKeyDown={handleKeyDown}
      >
        {selected?.icon && <span aria-hidden="true">{selected.icon}</span>}
        <span className="cx-select-value">{selected?.label ?? placeholder}</span>
        <ChevronDown className="cx-select-caret" size={14} aria-hidden="true" />
      </button>

      {disabled && disabledReason && (
        <span id={`${baseId}-reason`} className="cx-sr-only">
          {disabledReason}
        </span>
      )}

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="cx-select-menu"
          data-align={align === "end" ? "end" : undefined}
          aria-label={labelledBy ? undefined : label}
          aria-labelledby={labelledBy}
          tabIndex={-1}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${baseId}-opt-${index}`}
              role="option"
              className="cx-select-option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled || undefined}
              data-active={index === activeIndex ? "true" : undefined}
              onMouseEnter={() => !option.disabled && setActiveIndex(index)}
              onClick={() => commit(index)}
            >
              {option.icon && <span aria-hidden="true">{option.icon}</span>}
              <span>{option.label}</span>
              {option.hint && <span className="cx-caption">{option.hint}</span>}
              {option.value === value && (
                <Check className="cx-select-check" size={14} aria-hidden="true" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
